import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchCities } from '../../api/citiesApi'
import styles from './CityPickerDropdown.module.css'

interface CityPickerDropdownProps {
  currentCity: string
  currentLang: string
  onSelect: (citySlug: string) => void
}

export default function CityPickerDropdown({
  currentCity,
  currentLang,
  onSelect,
}: CityPickerDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [btnRect, setBtnRect] = useState<DOMRect | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => fetchCities(),
    staleTime: 5 * 60 * 1000,
  })

  const currentCityName =
    cities?.find((c) => c.slug === currentCity)?.name ?? currentCity

  // Close on click-outside (button or portal'd dropdown)
  useEffect(() => {
    if (!open) return
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node
      if (!btnRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Close on any scroll (including horizontal scroll of the menu bar)
  useEffect(() => {
    if (!open) return
    function handleScroll() { setOpen(false) }
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', handleScroll, { capture: true })
  }, [open])

  function handleToggle() {
    setOpen((v) => {
      if (!v && btnRef.current) {
        setBtnRect(btnRef.current.getBoundingClientRect())
      }
      return !v
    })
  }

  function handleSelect(slug: string) {
    setOpen(false)
    onSelect(slug)
  }

  return (
    <div className={styles.wrapper}>
      <button
        ref={btnRef}
        type="button"
        className={styles.btn}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('city')}
      >
        {currentCityName}
      </button>

      {open && btnRect && createPortal(
        <ul
          ref={dropdownRef}
          className={styles.dropdown}
          role="listbox"
          aria-label={t('city')}
          style={{ top: btnRect.bottom + 4, left: btnRect.left }}
        >
          {(cities ?? []).map((city) => {
            const available = city.output_languages.includes(currentLang)
            const unavailableTitle = available
              ? undefined
              : t('city_unavailable_tooltip', {
                  lang: currentLang,
                  langs: city.output_languages.join(', '),
                })
            return (
              <li key={city.slug} role="option" aria-selected={city.slug === currentCity}>
                <button
                  type="button"
                  className={`${styles.item} ${city.slug === currentCity ? styles.itemActive : ''} ${!available ? styles.itemDisabled : ''}`}
                  onClick={available ? () => handleSelect(city.slug) : undefined}
                  disabled={!available}
                  title={unavailableTitle}
                >
                  {city.name}
                </button>
              </li>
            )
          })}
        </ul>,
        document.body,
      )}
    </div>
  )
}
